import { DataQualityGrade } from '@lune-climate/lune'
import { ImprovementSuggestion, ImprovementSuggestionFact } from '@lune-fe/dapi-models'
import {
    getDataQualityScoreColor,
    getDataQualityScoreFormattedLetter,
} from '@lune-fe/lune-components-lib'
import { Text } from '@lune-fe/lune-ui-lib'
import { Box } from '@mui/material'
import { FC, useMemo } from 'react'

export const InputsToLabel: Record<ImprovementSuggestionFact, string> = {
    // ModeFact
    mode_sea_vessel_imo: 'Vessel IMO',
    mode_sea_trade_lane: 'Container ship and trade lane average',
    mode_sea_generic: 'Generic container ship',
    mode_air_aircraft_type: 'Flight number/Aircraft type',
    mode_air_cargo_or_passenger_plane: 'Generic cargo or passenger plane',
    mode_air_generic_plane: 'Generic plane',
    mode_road_vehicle_type_and_fuel: 'Vehicle type and fuel specified',
    mode_road_generic: 'Generic road travel',

    // RouteFact
    route_sea_ais: 'Vessel IMO for AIS tracking',
    route_sea_client_distance: 'Exact distance provided',
    route_sea_detailed_address: 'Exact address, coordinates or portcodes',
    route_sea_minimal_address: 'City-level only address',
    route_sea_carrier_schedules: 'Carrier schedule stops',
    route_air_client_distance: 'Exact distance provided',
    route_air_flight_number: 'Flight number',
    route_air_detailed_address: 'Exact address, coordinates or portcodes',
    route_air_minimal_address: 'City-level only address',
    route_road_client_distance: 'Exact distance provided',
    route_road_detailed_address: 'Exact address, coordinates or portcodes',
    route_road_minimal_address: 'City-level only address',
    route_rail_client_distance: 'Exact distance provided',
    route_rail_detailed_address: 'Exact address, coordinates or portcodes',
    route_rail_minimal_address: 'City-level only address',

    // ShipmentFact
    load_client_mass_ef_mass: 'Mass provided, EF has mass units',
    load_client_container_ef_container: 'TEU’s provided, EF has TEU units',
    load_client_cargo_type_conversion_required: 'Cargo type, conversion required',
    load_client_mass_ef_container: 'Mass provided, EF has TEU units',
    load_client_container_ef_mass: 'TEU’s provided, EF has mass units',
    load_client_mass_and_container: 'Both TEU and mass provided',
}

const tableStyle = {
    display: 'flex',
    gap: 3,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-between',
    '> .MuiTypography-root, > div': {
        lineHeight: '64px',
        '&:nth-child(1)': {
            width: '50%',
        },
        '&:nth-child(2)': {
            width: '50%',
        },
    },
    '> .MuiTypography-root': {
        lineHeight: '72px',
    },
}

const Bar: FC<{
    data: {
        fact: string
        score: string
        percentage: number
        recommended: boolean
        barColor: string
    }
}> = ({ data }) => {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: '100%',
            }}
        >
            <Text
                variant={'body2'}
                sx={{
                    fontWeight: 700,
                    minWidth: '40px',
                }}
            >
                {getDataQualityScoreFormattedLetter(data.score as DataQualityGrade)}
            </Text>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    ml: 1,
                    gap: 1,
                }}
            >
                <Box
                    sx={{
                        height: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        width: `${data.percentage}%`,
                    }}
                >
                    <Box
                        sx={{
                            width: '100%',
                            backgroundColor: data.barColor,
                            height: '100%',
                        }}
                    />
                </Box>
                <Text variant={'body3'} sx={{ color: `Grey600` }}>
                    {Math.round(data.percentage)}%
                </Text>
            </Box>
        </Box>
    )
}

type ImprovementTableItemProps = {
    fact: string
    percentage: number
    recommended: boolean
    score: string
    barColor: string
}

const ImprovementTableItem: FC<{
    data: ImprovementTableItemProps
    key: number
}> = ({ data, key }) => {
    return (
        <Box key={key} sx={tableStyle}>
            <Text
                variant={'body2'}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: 3 / 2,
                    flexWrap: 'wrap',
                }}
            >
                {data.fact}
                {data.recommended && (
                    <Text
                        variant={'caption'}
                        sx={{
                            color: `Green600`,
                            backgroundColor: `Green50`,
                            padding: '4px 6px',
                            borderRadius: '6px',
                        }}
                    >
                        Recommended
                    </Text>
                )}
            </Text>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                }}
            >
                <Bar data={data} />
            </Box>
        </Box>
    )
}

const scoreRank = {
    A_plus: 0,
    A: 1,
    A_minus: 2,
    B_plus: 3,
    B: 4,
    B_minus: 5,
    C_plus: 6,
    C: 7,
    C_minus: 8,
    D: 9,
} as const

/**
 * Sorts items by their data quality score,
 * then by whether they are recommended
 * then alphabetically by fact name.
 */
export function sortByScore<
    T extends Pick<ImprovementTableItemProps, 'fact' | 'score' | 'recommended'>,
>(items: readonly T[]): T[] {
    const scoreOrder: Record<string, number | undefined> = {
        'A+': 13,
        A: 12,
        'A-': 11,
        'A / B': 10,
        'B+': 9,
        B: 8,
        'B-': 7,
        'B / C': 6,
        'C+': 5,
        C: 4,
        'C-': 3,
        'C / D': 2,
        'D+': 1,
        D: 0,
        'D-': -1,
    }
    return [...items].sort((a, b) => {
        const scoreA = scoreOrder[a.score]
        const scoreB = scoreOrder[b.score]
        if (scoreA === undefined || scoreB === undefined) {
            throw new Error(`Unexpected score: ${a.score} or ${b.score}`)
        }
        const diff = scoreB - scoreA
        if (diff !== 0) {
            return diff
        }
        if (a.recommended !== b.recommended) {
            return a.recommended ? -1 : 1
        }
        return a.fact.localeCompare(b.fact)
    })
}

const trimTrailingZeroNotRecommended = <T extends { percentage: number; recommended: boolean }>(
    data: T[],
) => {
    const cleaned = [...data]
    while (cleaned.length) {
        const last = cleaned.at(-1)
        if (!last) break
        if (last.percentage < 0.5 && !last.recommended) {
            cleaned.pop()
        } else {
            break
        }
    }
    return cleaned
}

const ImprovementsTable: FC<{ data: ImprovementSuggestion }> = ({ data }) => {
    const localSuggestions = useMemo<ImprovementTableItemProps[]>(() => {
        if (data.label !== 'client') {
            const cleanedBreakdown = trimTrailingZeroNotRecommended(data.breakdown)
            return cleanedBreakdown.map((item) => ({
                fact: InputsToLabel[item.fact],
                percentage: item.percentage,
                recommended: item.recommended,
                score: getDataQualityScoreFormattedLetter(item.score as DataQualityGrade),
                barColor: getDataQualityScoreColor(item.score as DataQualityGrade),
            }))
        }
        const aggregated = data.breakdown.filter((b) =>
            [
                'load_client_mass_ef_mass',
                'load_client_container_ef_container',
                'load_client_mass_ef_container',
                'load_client_container_ef_mass',
            ].includes(b.fact),
        )

        const others = data.breakdown
            .filter(
                (b) =>
                    ![
                        'load_client_mass_ef_mass',
                        'load_client_container_ef_container',
                        'load_client_mass_ef_container',
                        'load_client_container_ef_mass',
                    ].includes(b.fact),
            )
            .map((item) => ({
                fact: InputsToLabel[item.fact],
                percentage: item.percentage,
                recommended: item.recommended,
                score: getDataQualityScoreFormattedLetter(item.score as DataQualityGrade),
                barColor: getDataQualityScoreColor(item.score as DataQualityGrade),
            }))

        if (aggregated.length === 0) {
            return sortByScore(trimTrailingZeroNotRecommended(others))
        }

        const sumPercentage = Math.round(aggregated.reduce((sum, i) => sum + i.percentage, 0))
        const recommended = aggregated.some((i) => i.recommended)

        const uniqueScoresSorted = Array.from(new Set(aggregated.map((item) => item.score))).sort(
            (a, b) => scoreRank[a] - scoreRank[b],
        )

        const uniqueScoresFormatted = uniqueScoresSorted.map((s) =>
            getDataQualityScoreFormattedLetter(s as DataQualityGrade),
        )

        const combined = [
            {
                fact: 'Either TEU or mass provided',
                percentage: sumPercentage,
                recommended,
                score: uniqueScoresFormatted.join(' / '),
                barColor: getDataQualityScoreColor(uniqueScoresSorted.at(-1) as DataQualityGrade),
            },
            ...others,
        ]

        return sortByScore(trimTrailingZeroNotRecommended(combined))
    }, [data])

    return (
        <Box>
            <Box sx={tableStyle}>
                <Text variant="button">Input</Text>
                <Text variant="button">Data Quality</Text>
            </Box>
            {localSuggestions.map((item, index) => (
                <ImprovementTableItem key={index} data={item} />
            ))}
        </Box>
    )
}

export default ImprovementsTable
